import type { Side, ViewerRole } from "../../shared/game.ts";

export interface RecentGame {
  id: string;
  role: ViewerRole;
  opponentName: string | null;
  updatedAt: number;
}

const NAME_KEY = "chess:player-name";
const RECENT_KEY = "chess:recent-games";
const SOUND_KEY = "chess:sound";
const PUSH_AUTO_KEY = "chess:push-auto";
const PROFILE_TOKEN_KEY = "chess:profile-token";

export function getSoundEnabled(): boolean {
  try {
    return localStorage.getItem(SOUND_KEY) !== "off";
  } catch {
    return true;
  }
}

export function setSoundEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(SOUND_KEY, enabled ? "on" : "off");
  } catch {
    // Sounds still work for the current session when storage is unavailable.
  }
}

export function getPushAutoEnabled(): boolean {
  try {
    return localStorage.getItem(PUSH_AUTO_KEY) !== "off";
  } catch {
    return true;
  }
}

export function setPushAutoEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(PUSH_AUTO_KEY, enabled ? "on" : "off");
  } catch {
    // Push permission remains controlled by the browser when storage is unavailable.
  }
}

export function getPushPreference(id: string): boolean {
  try {
    return localStorage.getItem(`chess:push:${id}`) === "enabled";
  } catch {
    return false;
  }
}

export function setPushPreference(id: string, enabled: boolean): void {
  try {
    if (enabled) localStorage.setItem(`chess:push:${id}`, "enabled");
    else localStorage.removeItem(`chess:push:${id}`);
  } catch {
    // Push remains optional when storage is unavailable.
  }
}

export function getPlayerName(): string {
  try {
    return localStorage.getItem(NAME_KEY) ?? "";
  } catch {
    return "";
  }
}

export function setPlayerName(name: string): void {
  try {
    localStorage.setItem(NAME_KEY, name);
  } catch {
    // The game still works when private browsing blocks storage.
  }
}

export function getProfileToken(): string | null {
  try {
    return localStorage.getItem(PROFILE_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setProfileToken(token: string): void {
  try {
    localStorage.setItem(PROFILE_TOKEN_KEY, token);
  } catch {
    // The current session can continue even if persistent storage is unavailable.
  }
}

export function clearProfileToken(): void {
  try {
    localStorage.removeItem(PROFILE_TOKEN_KEY);
  } catch {
    // Nothing else to clear.
  }
}

export function getGameToken(id: string): string | null {
  try {
    return localStorage.getItem(`chess:game-token:${id}`);
  } catch {
    return null;
  }
}

export function setGameToken(id: string, token: string): void {
  try {
    localStorage.setItem(`chess:game-token:${id}`, token);
  } catch {
    // Rejoining after a full reload may be unavailable, but the current session works.
  }
}

export function getRecentGames(): RecentGame[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (item): item is RecentGame =>
          Boolean(
            item &&
              typeof item === "object" &&
              "id" in item &&
              typeof item.id === "string" &&
              "updatedAt" in item &&
              typeof item.updatedAt === "number",
          ),
      )
      .slice(0, 5);
  } catch {
    return [];
  }
}

export function recordRecentGame(id: string, role: ViewerRole, opponentName: string | null = null): void {
  try {
    const recent = getRecentGames().filter((game) => game.id !== id);
    recent.unshift({ id, role, opponentName, updatedAt: Date.now() });
    localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, 5)));
  } catch {
    // Optional convenience only.
  }
}

export function opponentSide(role: ViewerRole): Side | null {
  if (role === "white") return "black";
  if (role === "black") return "white";
  return null;
}
